export const frontmatter = {
  title: "The new passwordless game",
  description: "Passwords are finally on the way out. Here's why passwordless wins, and how it actually works.",
  date: "2024-10-29",
  tags: ["industry", "opinions", "development"],
  readingTime: 6,
};

We're nearly at the end of 2024 and passwords are quietly dying. Those clunky strings of letters, numbers and a `!` we kept adding at the end — they're being replaced.

Anyone else tired of guessing if you capitalized the second letter of your first pet's name? Or worse, the small panic when a password you've used for years suddenly doesn't work on a new site? Same. So I'm glad this shift is finally happening.

How many "forgot my password" emails did you click this year? I lost count. Let's talk about what passwordless actually is, why it beats both old passwords and things like OAuth and SSO, and why this matters.

## Why passwords stopped working

Passwords made sense in the early internet. One site, one password, done. Then every site wanted you to create a unique one. Then add a special character. Then don't reuse the last twelve. It got exhausting.

Most of us cope by reusing the same handful of variations. Which is exactly what attackers count on — they don't guess passwords, they buy leaked ones and try them everywhere. The harder we tried to make passwords "strong," the easier this game got for them.

Password managers help, but they're another app you have to remember to open and trust.

<Callout emoji="🤔">
  For years the industry kept adding hoops to "secure" passwords, while passwords kept getting easier to break. Turns out the whole "make it stronger" game wasn't going to save us.
</Callout>

## So what is passwordless?

Passwordless means proving it's you with something more reliable than a string you typed. A face scan, a fingerprint, or a device you already trust like your phone or a hardware key. Things that are much harder to steal or guess.

The trick is using a few of these together. Three categories:

1. **Something you have** — your phone, a security key.
2. **Something you are** — fingerprint, face.
3. **Something you know** — a short PIN.

Use two or more and you've got real multi-factor auth. Like a guard asking for two IDs instead of one — much harder to fake.

## What about 2FA?

If you've enabled 2FA anywhere, you already get the idea: even if your password leaks, a second proof keeps the account locked.

Passwordless can use 2FA too. The difference is what the *first* factor is. Instead of a password plus a code, it's a fingerprint plus a tap on your phone. Same "two layers," but you've removed the weakest link entirely.

Think of passwordless as 2FA with the password finally taken out of the equation.

## And SSO?

Single Sign-On — those "Sign in with Google" buttons — has been a lifesaver. One trusted login, many apps. But there's a catch: behind that Google button is still a password. If your Google password leaks, every SSO-linked account leaks with it.

Passwordless can fix that too. Tie your SSO to biometrics or a hardware key instead of a password, and the whole chain gets a lot stronger. SSO doesn't go away — it just stops resting on a password.

<Callout emoji="🔍">
  SSO made things easier. Passwordless can make SSO actually safe.
</Callout>

## Pros and cons, the honest version

<ProsCons
  pros={[
    "Stronger than any password you'd actually remember.",
    "Logging in feels instant.",
    "No more password-reset support tickets.",
    "Fits how phones already work.",
    "You can't reuse a fingerprint across sites.",
  ]}
  cons={[
    "Biometrics aren't perfect.",
    "Sharing biometric data with companies has real privacy trade-offs.",
    "If you lose your trusted device, recovery matters.",
    "There's a small learning curve.",
    "Backup methods need to be set up properly.",
  ]}
/>

## Why I think this sticks

A few reasons it's actually catching on this time:

1. **Security.** Most breaches start with a weak or reused password. Take the password out, take that whole class of breach with it.
2. **Convenience.** No more "reset link" loops. One tap and you're in.
3. **Cost.** Companies spend a fortune on password support. Passwordless cuts that.
4. **It matches the world we live in.** We already unlock our phones with our face fifty times a day.

## Why it beats those "Login with X" buttons

OAuth feels passwordless from your side, but a password is still sitting on Google's or Facebook's servers. If that one falls, the dominoes go everywhere. True passwordless removes the password from *both* ends. Less to steal, less to leak.

## A few things worth knowing

1. **Biometrics are getting better.** Today's sensors are much harder to spoof than the early ones, and the templates usually never leave your device.
2. **Lock your device.** Passwordless leans heavily on devices, so a stolen unlocked phone is a real problem. PIN, biometric lock, whatever — use it.
3. **Set up backups.** Faces don't always cooperate. PINs, recovery codes, a second device — set them up before you need them.
4. **Read the privacy policy.** Know who's storing what. Most reputable systems keep biometric data on-device, but check.

## Why I'm in

I never thought I'd get this excited about logging in. Passwords were a necessary annoyance for two decades, and they just don't fit how we use the internet anymore. Passwordless is the first replacement that feels both safer *and* easier — usually those two pull in opposite directions.

There's a learning curve, sure. But if it means I never have to type "Fluffy@123!" again, I'm in.
